import React, { useState, useEffect, useMemo, useContext } from 'react';
import type { DeadlinesFilters } from '../../types';
import { Container, Card, Text, Button } from '../../design-system';
import { isEmpty, find } from 'lodash';
import { Block } from '../../design-system';
import {
  useToggle,
  useDeepCompareEffect,
  useLocation,
  useUpdateEffect,
} from 'react-use';
import { useData } from '../../helpers/data-loader';
import {
  errorMsg,
  checkPermissions,
  createSelectOptions,
} from '../../helpers/utils';
import ContainerHeader from '../../components/ContainerHeader';
import { Table } from '../../components/Table';
import {
  DEADLINE_COLUMNS,
  PERMISSIONS,
  getDeadlineRowId,
} from '../../globalConstants';
import { Session } from '../../contexts/sessionContext';
import {
  DEADLINE_OPTIONS,
  ACTIVE_OPTIONS,
  nameFromArray,
} from '../shared-constants';
import TableFilter from '../../components/TableFilter';
import ExportButton from '../../components/ExportButton';
import { DeadlinesContext, ACTIONS } from '../../contexts/deadlinesContext';
import Alert from '../../helpers/alert';
import useTableSort, { SavedSort } from '../../hooks/useTableSort';

// Must match the $sortColumn default in DeadlineController::index().
// If you change one, change the other.
const DEFAULT_DEADLINES_SORT: SavedSort = { id: 'deadline', desc: false };

const Deadlines = () => {
  const { initialSortBy, initialFilterParams, persistSort } = useTableSort(
    'deadlinesTableSort',
    DEFAULT_DEADLINES_SORT,
  );

  const location = useLocation();
  const [deadlines, fetchActionData, loadingActions] = useData('deadline', {
    errorMsg: errorMsg('actions'),
  });
  const [clientData, fetchClientData] = useData('/client', {
    errorMsg: errorMsg('clients'),
  });
  const [userData, fetchUserData] = useData('/user', {
    errorMsg: errorMsg('users'),
  });

  const {
    state: {
      user: { permissions, isClient },
    },
  } = useContext(Session);
  const {
    state: { search, filters },
    dispatch,
  } = useContext(DeadlinesContext);
  const [filterParams, setFetchParams] = useState(initialFilterParams);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, toggleOpen] = useToggle(false);
  const clientOptions = createSelectOptions(clientData);
  const userOptions = createSelectOptions(userData);

  const canViewTeamDeadlines = checkPermissions(permissions, [
    PERMISSIONS.READ_TEAM_DEADLINES,
  ]);

  const FILTER_FIELDS = [
    {
      name: 'client',
      label: 'Client',
      component: 'select',
      options: clientOptions,
    },
    {
      name: 'projectLead',
      label: 'Project Lead',
      component: 'select',
      options: userOptions,
      disabled: !canViewTeamDeadlines,
    },
    {
      name: 'assignee',
      label: 'Assignee',
      component: 'select',
      options: userOptions,
    },
    {
      name: 'deadline',
      label: 'Deadline',
      component: 'select',
      options: DEADLINE_OPTIONS,
      sortOptions: false,
      isClearable: false,
    },
    {
      name: 'active',
      label: 'Active Status',
      component: 'select',
      options: ACTIVE_OPTIONS,
      sortOptions: false,
      isClearable: true,
    },
  ];

  useDeepCompareEffect(() => {
    if (isEmpty(clientData)) fetchClientData();
    if (isEmpty(userData)) fetchUserData();
  }, [location]);

  const filtersToDisplay = useMemo(() => {
    return Object.entries(filters)
      .filter(([filter, value]) => {
        if (!value) {
          return false;
        }

        if (filter === 'deadline' && value === 0) {
          return false;
        }

        if (filter === 'status' && value === 'All') {
          return false;
        }

        return true;
      })
      .map(([filter, value]) => {
        let displayValue = value;

        if (filter === 'client') {
          displayValue = nameFromArray(value, clientData);
        }

        if (filter === 'projectLead' || filter === 'assignee') {
          displayValue = nameFromArray(value, userData);
        }

        if (filter === 'deadline') {
          const search_deadline = find(DEADLINE_OPTIONS, {
            value: Number(value),
          });
          if (search_deadline) {
            displayValue = search_deadline.label;
          } else {
            displayValue = `${value} days`;
          }
        }

        if (filter === 'active') {
          filter = 'status';
          const active_filter = find(ACTIVE_OPTIONS, {
            value,
          });
          if (active_filter) {
            displayValue = active_filter.label;
          }
        }

        return { filter, displayValue };
      });
  }, [clientData, userData, filters]);

  const makeReq = () => {
    fetchActionData({
      params: { ...filterParams, ...filters, search: searchValue },
    });
  };

  useUpdateEffect(() => {
    makeReq();
  }, [filterParams, searchValue, filters]);

  const clearFilters = () => {
    dispatch({ type: ACTIONS.CLEAR_FILTERS });
  };

  const saveFilters = (values: DeadlinesFilters) => {
    dispatch({ type: ACTIONS.SET_FILTERS, value: values });
    toggleOpen();
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handlePageChange = ({
    pageIndex,
    pageSize,
    sortColumn,
    sortDesc,
  }: {
    pageIndex: number;
    pageSize: number;
    sortColumn: any;
    sortDesc: any;
  }) => {
    persistSort(sortColumn, sortDesc);
    setFetchParams((prevState) => ({
      ...prevState,
      page: pageIndex + 1,
      page_size: pageSize,
      sortColumn,
      sortDesc,
    }));
  };

  const lackPermission = () => {
    Alert.error(
      'You are an external user and do not have permission to export data. Please contact the software administrator to generate this report for you.',
    );
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preset = Object.fromEntries(params.entries());
    // Apply any URL-preset filters directly. Don't call saveFilters here — it
    // toggles the filter modal open, which the old extra toggleOpen() then
    // closed, causing a flicker plus a duplicate SET_FILTERS dispatch.
    dispatch({
      type: ACTIONS.SET_FILTERS,
      value: { ...filters, ...preset } as DeadlinesFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container fluid gutter={50}>
      <Card mt={30}>
        <ContainerHeader>
          <Text fontSize={25}>Deadlines</Text>
          {isClient && (
            <Button variant="green" onClick={lackPermission}>
              Export
            </Button>
          )}
          {!isClient && <ExportButton type="deadlines" />}
        </ContainerHeader>
        <Block mb={3}>
          <TableFilter
            search={search}
            handleSearch={handleSearch}
            clearFilters={clearFilters}
            filtersToDisplay={filtersToDisplay}
            filters={filters}
            saveFilters={saveFilters}
            isOpen={isOpen}
            toggleModal={toggleOpen}
            filterFields={FILTER_FIELDS}
          />
        </Block>

        <Table
          onPageChange={handlePageChange}
          columns={DEADLINE_COLUMNS}
          data={deadlines}
          loading={loadingActions}
          initialSortBy={initialSortBy}
          getRowId={getDeadlineRowId}
        />
      </Card>
    </Container>
  );
};

export default Deadlines;
